import {getArtistLink} from '@app/web-player/artists/artist-link';
import {usePrimaryArtistForCurrentUser} from '@app/web-player/backstage/use-primary-artist-for-current-user';
import {webPlayerSidebarIcons} from '@app/web-player/layout/web-player-sidebar-icons';
import {BufferingIndicator} from '@app/web-player/player-controls/buffering-indicator';
import {useCuedTrack} from '@app/web-player/player-controls/use-cued-track';
import {playerOverlayState} from '@app/web-player/state/player-overlay-store';
import {TrackImage} from '@app/web-player/tracks/track-image/track-image';
import {useAuth} from '@common/auth/use-auth';
import {CustomMenuItem} from '@common/menus/custom-menu';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useCurrentTime} from '@common/player/hooks/use-current-time';
import {usePlayerStore} from '@common/player/hooks/use-player-store';
import {NextButton} from '@common/player/ui/controls/next-button';
import {PlayButton} from '@common/player/ui/controls/play-button';
import {PreviousButton} from '@common/player/ui/controls/previous-button';
import {NavbarAuthMenu} from '@common/ui/navigation/navbar/navbar-auth-menu';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Badge} from '@ui/badge/badge';
import {Item} from '@ui/forms/listbox/item';
import {Trans} from '@ui/i18n/trans';
import {LucideIcon} from '@ui/icons/lucide/lucide-icon-wrapper';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {ProgressBar} from '@ui/progress/progress-bar';
import {useSettings} from '@ui/settings/use-settings';
import clsx from 'clsx';
import {CircleUser, MicVocalIcon} from 'lucide-react';
import {useMemo} from 'react';

export function MobilePlayerControls() {
  return (
    <div
      className={clsx(
        'fixed inset-x-0 bottom-0 z-[50] isolate',
        'border-t border-divider-lighter bg-elevated shadow-[0_-4px_24px_rgba(0,0,0,0.12)]',
        'dark:border-divider dark:shadow-[0_-4px_24px_rgba(0,0,0,0.45)]',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <PlayerControls />
      <MobileNavbar />
    </div>
  );
}

function PlayerControls() {
  const mediaIsCued = usePlayerStore(s => s.cuedMedia != null);
  if (!mediaIsCued) return null;

  return (
    <div
      className="relative flex min-h-[68px] items-center justify-between gap-12 px-12 py-10 md:min-h-72 md:gap-20 md:px-20 md:py-12"
      onClick={() => {
        playerOverlayState.toggle();
      }}
    >
      <QueuedTrack />
      <PlaybackButtons />
      <PlayerProgressBar />
    </div>
  );
}

function QueuedTrack() {
  const track = useCuedTrack();

  if (!track) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-auto items-center gap-12 md:gap-14">
      <TrackImage
        className="h-44 w-44 flex-shrink-0 rounded-md object-cover shadow-sm md:h-48 md:w-48"
        track={track}
      />
      <div className="min-w-0 flex-auto overflow-hidden">
        <div className="overflow-hidden overflow-ellipsis whitespace-nowrap text-sm font-semibold md:text-base">
          {track.name}
        </div>
        <div className="mt-2 overflow-hidden overflow-ellipsis whitespace-nowrap text-xs text-muted md:text-sm">
          {track.artists?.map(a => a.name).join(', ')}
        </div>
      </div>
    </div>
  );
}

function PlaybackButtons() {
  return (
    <div className="flex flex-shrink-0 items-center justify-center gap-4 md:gap-6">
      <PreviousButton stopPropagation size="md" iconSize="lg" />
      <div className="relative isolate mx-2 md:mx-4">
        <BufferingIndicator />
        <PlayButton size="lg" iconSize="xl" stopPropagation />
      </div>
      <NextButton stopPropagation size="md" iconSize="lg" />
    </div>
  );
}

function PlayerProgressBar() {
  const duration = usePlayerStore(s => s.mediaDuration);
  const playerReady = usePlayerStore(s => s.providerReady);
  const currentTime = useCurrentTime();

  if (!playerReady || !duration) {
    return (
      <ProgressBar
        size="xs"
        className="absolute bottom-0 left-0 right-0"
        trackColor="bg-divider"
        progressColor="bg-primary"
        trackHeight="h-[4px] md:h-[5px]"
        radius="rounded-none"
        isIndeterminate
      />
    );
  }

  return (
    <ProgressBar
      size="xs"
      className="absolute bottom-0 left-0 right-0"
      trackColor="bg-divider"
      progressColor="bg-primary"
      trackHeight="h-[4px] md:h-[5px]"
      radius="rounded-none"
      minValue={0}
      maxValue={duration}
      value={currentTime}
    />
  );
}

function MobileNavbar() {
  const menu = useCustomMenu('mobile-bottom');
  if (!menu) return null;

  const itemCount = menu.items.length + 1;

  return (
    <nav
      className="grid items-center gap-4 border-t border-divider-lighter/80 px-8 py-8 dark:border-divider"
      style={{gridTemplateColumns: `repeat(${itemCount}, minmax(0, 1fr))`}}
      aria-label="Main"
    >
      {menu.items.map(item => (
        <CustomMenuItem
          unstyled
          key={item.id}
          item={item}
          defaultIcons={webPlayerSidebarIcons}
          iconSize="md"
          className={({isActive}) =>
            clsx(
              'flex flex-col items-center justify-center gap-6 rounded-xl px-4 py-8 text-center text-[11px] font-medium leading-none transition-colors md:text-xs',
              isActive
                ? 'font-semibold text-primary'
                : 'text-muted hover:text-main',
            )
          }
          iconClassName="flex h-28 w-28 flex-shrink-0 items-center justify-center"
        />
      ))}
      <MobileAccountNavItem />
    </nav>
  );
}

function MobileAccountNavItem() {
  const {user} = useAuth();
  const hasUnreadNotif = !!user?.unread_notifications_count;
  const navigate = useNavigate();
  const {registration} = useSettings();

  const primaryArtist = usePrimaryArtistForCurrentUser();
  const {player} = useSettings();
  const menuItems = useMemo(() => {
    if (primaryArtist) {
      return [
        <MenuItem
          value="author"
          key="author"
          startIcon={<LucideIcon icon={MicVocalIcon} size="sm" />}
          onSelected={() => {
            navigate(getArtistLink(primaryArtist));
          }}
        >
          <Trans message="Artist profile" />
        </MenuItem>,
      ];
    }
    if (player?.show_become_artist_btn) {
      return [
        <MenuItem
          value="author"
          key="author"
          startIcon={<LucideIcon icon={MicVocalIcon} size="sm" />}
          onSelected={() => {
            navigate('/backstage/requests');
          }}
        >
          <Trans message="Become an author" />
        </MenuItem>,
      ];
    }

    return [];
  }, [primaryArtist, navigate, player?.show_become_artist_btn]);

  const button = (
    <button
      type="button"
      className="relative flex w-full flex-col items-center justify-center gap-6 rounded-xl px-4 py-8 text-center text-muted transition-colors hover:text-main"
    >
      <span className="flex h-28 w-28 flex-shrink-0 items-center justify-center">
        <LucideIcon icon={CircleUser} size="md" />
      </span>
      {hasUnreadNotif ? (
        <Badge className="-top-2" right="right-6">
          {user?.unread_notifications_count}
        </Badge>
      ) : null}
      <span className="w-full overflow-hidden overflow-ellipsis whitespace-nowrap text-[11px] font-medium leading-none md:text-xs">
        <Trans message="Account" />
      </span>
    </button>
  );

  if (!user) {
    return (
      <MenuTrigger>
        {button}
        <Menu>
          <Item value="login" onSelected={() => navigate('/login')}>
            <Trans message="Login" />
          </Item>
          {!registration?.disable && (
            <Item value="register" onSelected={() => navigate('/register')}>
              <Trans message="Register" />
            </Item>
          )}
        </Menu>
      </MenuTrigger>
    );
  }

  return <NavbarAuthMenu items={menuItems}>{button}</NavbarAuthMenu>;
}
